import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from '@/components/ui/Icons';
import type { Reader } from '@/api/client';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ReaderCatalogReaderTagsSubmenuPanel } from '@/components/ReadersTable/ReaderCatalogReaderTagsSubmenuPanel';

const SUBMENU_CLOSE_MS = 160;

export interface ReaderCatalogReaderTagsMenuCascadeRowProps {
  reader: Reader;
  disabled?: boolean;
  onCloseRootMenu: () => void;
  /** Модалка справочника тегов (создание и др.) */
  onOpenCatalogModal: () => void;
}

/** Первый уровень: «Теги чтеца » с разворотом подменю; второй уровень — см. ReaderCatalogReaderTagsSubmenuPanel. */
export const ReaderCatalogReaderTagsMenuCascadeRow = memo(function ReaderCatalogReaderTagsMenuCascadeRow({
  reader,
  disabled,
  onCloseRootMenu,
  onOpenCatalogModal,
}: ReaderCatalogReaderTagsMenuCascadeRowProps) {
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const scheduleSubmenuClose = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setSubmenuAnchor(null);
      timerRef.current = null;
    }, SUBMENU_CLOSE_MS);
  }, []);

  const cancelSubmenuClose = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <li
      className="context-menu__list-item"
      onMouseEnter={(e) => {
        if (disabled) return;
        cancelSubmenuClose();
        setSubmenuAnchor(e.currentTarget);
      }}
      onMouseLeave={scheduleSubmenuClose}
    >
      <button
        type="button"
        className="context-menu__item context-menu__action_with-sub"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(submenuAnchor)}
        role="menuitem"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          cancelSubmenuClose();
          const row = e.currentTarget.closest('.context-menu__list-item');
          if (!(row instanceof HTMLElement)) return;
          setSubmenuAnchor((prev) => (prev === row ? null : row));
        }}
      >
        <span className="context-menu__iconCell" aria-hidden>
          <i className="fa-solid fa-tags editable-cell__context-menu-fa" />
        </span>
        <span className="context-menu__item-text">Теги чтеца</span>
        <ChevronRightIcon size={16} className="context-menu__action-chevron" aria-hidden />
      </button>
      {submenuAnchor ? (
        <ContextMenuSubmenu
          anchorEl={submenuAnchor}
          onMouseEnter={cancelSubmenuClose}
          onMouseLeave={scheduleSubmenuClose}
        >
          <ReaderCatalogReaderTagsSubmenuPanel
            reader={reader}
            disabled={disabled}
            onOpenCatalogModal={() => {
              onCloseRootMenu();
              onOpenCatalogModal();
            }}
          />
        </ContextMenuSubmenu>
      ) : null}
    </li>
  );
});
